import React, { useMemo } from 'react';
import { Button, Popconfirm, Select, Tag, Tooltip } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { ListBase, useDataProvider, useGetIdentity, useListContext, useNotify } from 'react-admin';
import { FilterField } from '@src/components/Common/DataList';
import { NetOrcaTableListV1, TableListColumn } from '@src/components/Common/NetOrcaTableList';
import { getTagForRole } from '@src/components/Common/Tags';
import { getInitials } from '@src/components/Common/utils';
import { formatDateAndTime, formatDateAndTimeFromNow } from '@src/helpers/utils';
import NewMemberButton, { ASSIGNABLE_ROLES } from './NewMemberButton';
import { ExtendedDataProvider } from '@src/types';

const { Option } = Select;

/**
 * The member list under a name of its own when it is the admin's.
 *
 * Same endpoint as the team's own tab, different react-admin resource: a list's
 * filters are stored against its resource, and this is the same tab in both
 * points of view, so one name would let a team picked here follow the reader
 * back to a list that only ever holds one team.
 */
const ADMIN_RESOURCE = 'admin_team_memberships';

/** the roles a platform admin may set from here — the same set the form grants */
const ROLES = ASSIGNABLE_ROLES;

/**
 * Every role the endpoint can return, for the Role filter. CONFIGURATION stands
 * for a team's own machine account rather than a person; the list endpoint
 * excludes it, so it is filterable but not settable.
 */
const FILTERABLE_ROLES = [...ROLES, 'CONFIGURATION'];

/** One member under the shared column header. */
const MemberRow = ({
    record,
    self,
    admin,
    onRole,
    onRemove,
}: {
    record: any;
    self: boolean;
    admin: boolean;
    onRole: (membership: any, role: string) => void;
    onRemove: (membership: any) => void;
}) => (
    <div className={`tm-row${self ? ' tm-row--self' : ''}`}>
        <div className="tm-cell" data-label="Member">
            <span className="tm-who">
                <span className="tm-who__badge" aria-hidden="true">
                    {getInitials(record.username || '?').slice(0, 2)}
                </span>
                <span className="tm-who__name">{record.username}</span>
                {self && <Tag color="green">You</Tag>}
            </span>
        </div>

        <Tooltip placement="topLeft" title={record.email || undefined}>
            <div className={`tm-cell${record.email ? '' : ' tm-cell--empty'}`} data-label="Email">
                {record.email || '—'}
            </div>
        </Tooltip>

        <div className="tm-cell" data-label="Role">
            {/* A role this list cannot set — a team's own machine account —
                stays a tag, so it is shown rather than offered. */}
            {admin && ROLES.includes(record.role) ? (
                <Select
                    value={record.role}
                    size="small"
                    className="tm-role-select"
                    aria-label={`Role of ${record.username} in ${record.team_name}`}
                    onChange={(value: string) => onRole(record, value)}
                >
                    {ROLES.map((role) => (
                        <Option key={role} value={role}>
                            {role}
                        </Option>
                    ))}
                </Select>
            ) : (
                getTagForRole(record.role)
            )}
        </div>

        <Tooltip title={record.created ? formatDateAndTime(record.created) : undefined}>
            <div className="tm-cell tm-cell--muted" data-label="Joined">
                {record.created ? formatDateAndTimeFromNow(record.created) : '—'}
            </div>
        </Tooltip>

        {admin && (
            <div className="tm-cell tm-actions" data-label="">
                {/* No tooltip on the button: the confirm that opens from it
                    says the same thing at greater length, and the tooltip came
                    up over the confirm's own Remove button. */}
                <Popconfirm
                    title={`Remove ${record.username} from ${record.team_name}?`}
                    okText="Remove"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => onRemove(record)}
                >
                    <Button
                        type="text"
                        size="small"
                        danger
                        className="tm-actions__btn"
                        aria-label={`Remove ${record.username} from ${record.team_name}`}
                        icon={<DeleteOutlined />}
                    />
                </Popconfirm>
            </div>
        )}
    </div>
);

const MembersView = ({
    admin,
    teamId,
    teamName,
}: {
    admin: boolean;
    teamId?: number;
    teamName?: string;
}) => {
    const { identity } = useGetIdentity();
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const notify = useNotify();
    /**
     * The list's own refetch, not the global `useRefresh()`.
     *
     * This is what every other list on the page writes through, and the reason
     * matters: `refresh()` called from outside the `ListBase` re-read the list
     * after a role change but not after a delete, so a removed member stayed on
     * screen until the page was reloaded — the DELETE had answered 204 and the
     * notification had already said so. `refetch` belongs to this list and
     * re-reads it either way.
     */
    const { refetch } = useListContext();

    const onError = (error: any, fallback: string) => {
        const body = error?.body;
        const message = body?.detail || body?.role || error?.message || fallback;
        notify(Array.isArray(message) ? message[0] : String(message), 'warning');
    };

    const onRole = (membership: any, role: string) => {
        // updateMany PATCHes, which is what a partial role change needs --
        // update() PUTs and the serializer would then demand every field back.
        dataProvider
            .updateMany(ADMIN_RESOURCE, { ids: [membership.id], data: { role } })
            .then(() => {
                notify(`${membership.username} is now ${role} in ${membership.team_name}.`);
                refetch();
            })
            .catch((error: any) => onError(error, 'Could not change the role.'));
    };

    const onRemove = (membership: any) => {
        dataProvider
            .delete(ADMIN_RESOURCE, { id: membership.id })
            .then(() => {
                notify(`${membership.username} removed from ${membership.team_name}.`);
                refetch();
            })
            .catch((error: any) => onError(error, 'Could not remove the member.'));
    };

    /**
     * What the API's MembershipFilterSet accepts, minus the team: which team
     * this is is the page's question now, asked once on the tab strip, so it is
     * not asked again here. Role is filterable in the admin view and not on a
     * team's own tab, where it would be a filter over a handful of rows the
     * reader can already see.
     */
    const filterFields: FilterField[] = useMemo(
        () => [
            {
                name: 'username',
                label: 'Member',
                type: 'search',
                placeholder: 'Search by username',
                dataCy: 'username-search',
            },
            ...(admin
                ? ([
                    {
                        name: 'role',
                        label: 'Role',
                        type: 'select',
                        options: FILTERABLE_ROLES.map((role) => ({ value: role, label: role })),
                        placeholder: 'Any role',
                        dataCy: 'role-filter',
                    },
                ] as FilterField[])
                : []),
        ],
        [admin],
    );

    const columns: TableListColumn[] = useMemo(
        () => [
            { label: 'Member', filters: ['username'], dataCy: 'column-member' },
            { label: 'Email', dataCy: 'column-email' },
            { label: 'Role', filters: admin ? ['role'] : undefined, dataCy: 'column-role' },
            { label: 'Joined', dataCy: 'column-joined' },
        ],
        [admin],
    );

    return (
        <NetOrcaTableListV1
            className={`tm-list tm-list--members${admin ? ' tm-list--members-admin' : ''}`}
            dataCy={admin ? 'admin-member-list' : 'team-member-list'}
            fields={filterFields}
            columns={columns}
            template={admin ? 'var(--tm-member-admin-cols)' : 'var(--tm-member-cols)'}
            emptyText="No members found"
            noun="member"
            drawerTitle="Filter members"
            /* The column of row actions needs no heading — a delete button says
               what it is. The space over it goes to the one action that is
               about the list rather than about a row. */
            headerTrailing={admin
                ? <NewMemberButton teamId={teamId} teamName={teamName} resource={ADMIN_RESOURCE} />
                : undefined}
            renderRow={(record) => (
                <MemberRow
                    key={record.id}
                    record={record}
                    admin={admin}
                    self={!!identity?.username && identity.username === record.username}
                    onRole={onRole}
                    onRemove={onRemove}
                />
            )}
        />
    );
};

/**
 * Who is on the team, and what each of them may do.
 *
 * Two lists, one shape. To a member it is who else is here: the role is a tag,
 * because on their own team it is something to know rather than something to
 * change. To a platform admin it is every membership on the platform, and the
 * things that were labels become controls — the team leads the row and can be
 * filtered on, the role is set from the row it describes, and a membership can
 * be ended from the end of it. Nothing moves between the two, so the row reads
 * the same either way.
 *
 * Every write is enforced and audited server side; `all_teams` is honoured for
 * platform admins alone.
 *
 * Configuration memberships are excluded by the endpoint itself — they stand
 * for a team's own machine account rather than a person.
 */
const MembersTab = ({ admin = false, teamId, teamName }: { admin?: boolean; teamId?: number; teamName?: string }) => (
    <div className="tm-tab">
        <ListBase
            // Remounted per team, because react-admin keeps a list's page
            // and records against its resource: without this, switching
            // team shows the previous one's members until the fetch lands.
            key={teamId}
            resource={admin ? ADMIN_RESOURCE : 'team_memberships'}
            basePath="/teams"
            sort={{ field: 'created', order: 'ASC' }}
            perPage={25}
            /* `all_teams` is what lets an admin read a team they are not in;
               `team_id` is the team picked on the tab strip. */
            filter={admin ? { all_teams: true, team_id: teamId } : undefined}
        >
            {/* The writes live inside here rather than around it, so they can
                re-read the list through its own context. */}
            <MembersView admin={admin} teamId={teamId} teamName={teamName} />
        </ListBase>
    </div>
);

export default MembersTab;
